import { FC } from "react";
import { ShieldPlusIcon } from "lucide-react-native";

import { VnBox } from "@/components/VnBox";
import { VnIcon } from "@/components/VnIcon";
import { VnText } from "@/components/VnText/VnText";
import { OnboardingScreenType } from "@/domain/onboarding";
import {
  OnboardingNextStepsSectionType,
  useOnboardingNextStepsContent,
} from "@/hooks/useOnboardingNextStepsContent";
import { cn } from "@/utils/tailwind";

import { OnboardingHeader } from "./OnboardingHeader";
import { OnboardingStepFrame } from "./OnboardingStepFrame";

const Section: FC<OnboardingNextStepsSectionType> = ({
  number,
  title,
  description,
  duration,
  isDurationHidden,
}) => {
  const isFirstSection = number === 1;

  return (
    <VnBox className="flex w-full flex-row items-center justify-between gap-2">
      <VnBox className="flex min-w-0 flex-1 flex-row items-center gap-2">
        <VnBox
          className={cn(
            "h-14 w-14 shrink-0 items-center justify-center rounded-full",
            {
              "bg-sidebar": isFirstSection,
              "bg-secondary": !isFirstSection,
            },
          )}>
          <VnText
            className={cn("text-xl text-black/60", {
              "text-secondary": isFirstSection,
            })}
            variant="lgBold">
            {number}
          </VnText>
        </VnBox>
        <VnBox className="flex min-w-0 shrink flex-col">
          <VnText {...title} variant="lgBold" className="text-black/60" />
          <VnText {...description} className="text-muted-foreground" />
        </VnBox>
      </VnBox>
      {!isDurationHidden && (
        <VnBox
          className={cn("shrink-0 rounded-full px-2 py-0.5", {
            "bg-sidebar": isFirstSection,
            "bg-secondary": !isFirstSection,
          })}>
          <VnText
            variant="mdBold"
            className={cn({
              "text-secondary": isFirstSection,
              "text-sidebar": !isFirstSection,
            })}
            {...duration}
          />
        </VnBox>
      )}
    </VnBox>
  );
};

export const OnboardingNextStepsStep: FC<OnboardingScreenType> = layout => {
  const { title, description, sections } = useOnboardingNextStepsContent();

  return (
    <OnboardingStepFrame {...layout}>
      <VnBox className="bg-background w-full flex-1 items-center px-6 pt-16">
        <VnBox className="mb-8 w-full gap-6 md:max-w-screen-sm">
          <OnboardingHeader
            iconComponent={<VnIcon as={ShieldPlusIcon} size={48} />}
            titleI18nKey={title.i18nKey}
            titleI18nOptions={title.i18nOptions}
            descriptionI18nKey={description.i18nKey}
            descriptionI18nOptions={description.i18nOptions}
          />

          <VnBox className="relative w-full gap-6">
            {/* Vertical connecting line through circle centers */}
            <VnBox className="absolute bottom-7 left-[27px] top-7 w-0.5 bg-black/30" />
            {sections.map(section => (
              <Section key={section.number} {...section} />
            ))}
          </VnBox>
        </VnBox>
      </VnBox>
    </OnboardingStepFrame>
  );
};
